import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import ReactPlayer from 'react-player';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import APIHandler from 'config/APIHandler';
import StorageHandler from 'config/StorageHandler';

import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

import { storageKeys } from 'settings';

/**
 * Component for displaying intro chapter popup with auto-close video, only shown if the intro chapters have not been
 * marked as viewed in local storage.
 *
 * @param {object} props Props
 * @param {Array} props.chapters - Array of chapter objects.
 * @returns {JSX.Element} - IntroChapterPopup
 */
const IntroChapterPopup = ({ chapters }) => {
  const [introChapters, setIntroChapters] = useState([]);
  const [isPlayerVisible, setIsPlayerVisible] = useState(false);

  const { appId } = useSettings();
  const { user, updateUser } = useUserInfo();

  useEffect(() => {
    const getIntroChapters = async () => {
      try {
        const chapterVideoStatus = await StorageHandler.load(storageKeys.introChapters);
        const filteredChapters = chapters.filter((chapter) => chapter.introChapter && !chapterVideoStatus[chapter.id]);
        setIntroChapters(filteredChapters);
        console.log('chapters:', chapterVideoStatus);

        if (!chapterVideoStatus) {
          setIsPlayerVisible(true); // Show player if there are intro chapters not yet watched
        }
      } catch (error) {
        console.error('Failed to load intro chapters from storage:', error);
        setIntroChapters([]);
      }
    };

    getIntroChapters();
  }, [chapters]);

  const handleVideoEnd = async () => {
    setIsPlayerVisible(false);
    if (introChapters.length > 0) {
      const chapterVideoStatus = (await StorageHandler.load(storageKeys.introChapters)) || {};
      StorageHandler.save(storageKeys.introChapters, {
        ...chapterVideoStatus,
        [introChapters[0].id]: true,
      });

      const updateInfo = {
        chapterId: introChapters[0].id,
        progress: introChapters[0].chapterItemCount,
        lastPage: introChapters[0].chapterItemCount,
        email: user.email,
      };

      const d = new Date(Date.now());
      const leading = (val) => `0${val}`.slice(-2);
      const date = `${d.getFullYear()}-${leading(d.getMonth() + 1)}-${leading(d.getDate())}`;

      await APIHandler.setUserProgress(updateInfo);
      await APIHandler.setChapterCompleted(appId, user.email, introChapters[0].id, introChapters[0].chapterNumber);
      await updateUser(appId, [
        { param: 'training_started', val: date },
        { param: 'sales_companion_registered', val: date },
      ]);
    }
  };

  const { lang } = useSettings();
  const { langFile } = lang;
  const locale = `${langFile}-${langFile.toUpperCase()}`;
  let videoUrl = '';
  if (isPlayerVisible) {
    videoUrl = introChapters[0].video;
    if (locale !== 'en-EN' && videoUrl && videoUrl.includes('.mp4')) {
      // Replace .mp4 with _{locale}.mp4
      videoUrl = videoUrl.replace(/.mp4$/, `_${locale}.mp4`);
    }
  }

  return (
    isPlayerVisible && (
      <div className="intro-chapter-popup">
        <button onClick={() => handleVideoEnd()}>
          <FontAwesomeIcon icon={['fas', 'times']} />
        </button>
        <div className="background" onClick={() => handleVideoEnd()} />
        <ReactPlayer url={videoUrl} playing={isPlayerVisible} controls onEnded={handleVideoEnd} />
      </div>
    )
  );
};

export default IntroChapterPopup;

IntroChapterPopup.propTypes = {
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      introChapter: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]).isRequired,
      video: PropTypes.string,
    }),
  ).isRequired,
};
